import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { StickyNote, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ContactNotesThread from './ContactNotesThread';

/**
 * Reusable "Notes" affordance for any list that shows a person — portal buyers,
 * analytics drill-downs, the Who-to-Call panel, open-home attendees, etc. Many
 * of these rows are not yet CRM contacts, so opening the panel resolves
 * (get-or-creates) a Contact on the backend, then loads the person's note
 * thread — every note ever left about them, each stamped with its author and
 * date, newest first.
 *
 * The thread itself lives in ContactNotesThread, which the Open Home qualify
 * drawer renders inline. Same component, same endpoint, same notes: that is
 * what keeps the open-home tab and the property tab in step.
 *
 * Props:
 *   identity   { contactId?, attendeeId?, buyerId?, email?, name?, phone? } —
 *              at least one of contactId / attendeeId / email / buyerId is
 *              needed to attach notes.
 *   label      optional button text (default "Notes")
 *   compact    icon-only trigger (for tight rows)
 *   className  extra classes on the trigger button
 *   suggestPreferences  offer the buying preferences a new note mentions (buyer
 *              surfaces only, see ContactNotesThread)
 */
const ContactNotesButton = ({
  identity = {}, label = 'Notes', compact = false, className = '', onNoteChange,
  suggestPreferences = false, onPreferencesApplied,
}) => {
  const [open, setOpen] = useState(false);
  const [contactName, setContactName] = useState(identity.name || '');

  const canOpen = !!(
    identity.attendeeId ||
    identity.contactId ||
    identity.buyerId ||
    (identity.email && identity.email.includes('@'))
  );

  const handleOpen = (e) => {
    e?.stopPropagation?.();
    if (!canOpen) {
      toast.error('No email on file for this person — add them as a contact first.');
      return;
    }
    setOpen(true);
  };

  const handleClose = (e) => {
    e?.stopPropagation?.();
    setOpen(false);
  };

  const modal = open
    ? createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
          onClick={handleClose}
        >
          {/* Same shape as the Find Buyers buyer popup (BuyerActionsModal):
              header pinned, and ONE scroll region below it. The tall floor this
              used to carry didn't help, because the notes list was the only
              thing that flexed - the composer and the reminder presets took
              their full height first and left the history a two-line sliver you
              had to scroll (Joshua Diokno, CRM-2026-00050). Now the history
              renders at its natural height and the whole panel scrolls, so
              every note is on screen at once. */}
          <div
            className="flex w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl max-h-[88vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 p-4">
              <div className="flex items-center gap-2 min-w-0">
                <div className="rounded-lg bg-amber-100 p-1.5 flex-shrink-0">
                  <StickyNote className="h-4 w-4 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-bold text-gray-900">Agent Notes</h3>
                  {contactName && (
                    <p className="truncate text-xs text-gray-500">{contactName}</p>
                  )}
                </div>
              </div>
              <button
                onClick={handleClose}
                className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <ContactNotesThread
                identity={identity}
                onNoteChange={onNoteChange}
                suggestPreferences={suggestPreferences}
                onPreferencesApplied={onPreferencesApplied}
                onLoaded={({ contactName: name }) => setContactName(name || identity.name || '')}
              />
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        title="View / add notes"
        className={
          className ||
          `inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-amber-300 hover:text-amber-600 ${
            compact ? '' : ''
          }`
        }
      >
        <StickyNote className="h-3.5 w-3.5" />
        {!compact && label}
      </button>
      {modal}
    </>
  );
};

export default ContactNotesButton;
